import React, { memo, useMemo, useRef, useEffect } from 'react';
import { Card, Row, Col, Spin } from 'antd';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { LoadingOutlined } from '@ant-design/icons';
import { PERFORMANCE_CONFIG } from '../../config/performanceDashboard';
import './PerformanceDashboard.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const PerformanceCharts = memo(({ 
  performanceData, 
  loading = false, 
  className = '',
  style = {},
  dataPoints = 50 
}) => {
  const chartRefs = useRef({});

  // Generate mock time series data for demonstration
  const generateTimeSeriesData = (currentValue, dataPoints) => {
    const data = [];
    const labels = [];
    const now = new Date();
    
    for (let i = dataPoints - 1; i >= 0; i--) {
      const time = new Date(now.getTime() - i * 5000); // 5 second intervals
      labels.push(time.toLocaleTimeString());
      
      // Generate realistic variation around current value
      const variation = (Math.random() - 0.5) * (currentValue * 0.1);
      data.push(Math.max(0, currentValue + variation));
    }
    
    return { labels, data };
  };

  // Event Loop Latency Chart
  const eventLoopChartData = useMemo(() => {
    if (!performanceData?.eventLoopLatency) return null;
    
    const { labels, data } = generateTimeSeriesData(
      performanceData.eventLoopLatency.current, 
      dataPoints
    );
    
    return {
      labels,
      datasets: [
        {
          label: 'Event Loop Latency (ms)',
          data,
          borderColor: PERFORMANCE_CONFIG.CHART_CONFIG.COLORS.PRIMARY,
          backgroundColor: PERFORMANCE_CONFIG.CHART_CONFIG.BACKGROUND_COLORS.PRIMARY,
          fill: true,
          tension: 0.4,
          pointRadius: 2,
          pointHoverRadius: 4,
        },
      ],
    };
  }, [performanceData?.eventLoopLatency, dataPoints]);

  // Memory Usage Chart
  const memoryChartData = useMemo(() => {
    if (!performanceData?.memory) return null;
    
    const { labels, data: heapData } = generateTimeSeriesData(
      performanceData.memory.processHeap.percentage, 
      dataPoints
    );
    const { data: systemData } = generateTimeSeriesData(
      performanceData.memory.systemMemory.percentage, 
      dataPoints
    );
    
    return {
      labels,
      datasets: [
        {
          label: 'Process Heap (%)',
          data: heapData,
          borderColor: PERFORMANCE_CONFIG.CHART_CONFIG.COLORS.SUCCESS,
          backgroundColor: PERFORMANCE_CONFIG.CHART_CONFIG.BACKGROUND_COLORS.SUCCESS,
          fill: true,
          tension: 0.4,
          pointRadius: 2,
          pointHoverRadius: 4,
        },
        {
          label: 'System Memory (%)',
          data: systemData,
          borderColor: PERFORMANCE_CONFIG.CHART_CONFIG.COLORS.WARNING,
          backgroundColor: PERFORMANCE_CONFIG.CHART_CONFIG.BACKGROUND_COLORS.WARNING,
          fill: true,
          tension: 0.4,
          pointRadius: 2,
          pointHoverRadius: 4,
        },
      ],
    };
  }, [performanceData?.memory, dataPoints]);

  // CPU Usage Chart
  const cpuChartData = useMemo(() => {
    if (!performanceData?.cpu) return null;
    
    const { labels, data } = generateTimeSeriesData(
      performanceData.cpu.usage, 
      dataPoints
    );
    
    return {
      labels,
      datasets: [
        {
          label: 'CPU Usage (%)',
          data,
          borderColor: PERFORMANCE_CONFIG.CHART_CONFIG.COLORS.ERROR,
          backgroundColor: PERFORMANCE_CONFIG.CHART_CONFIG.BACKGROUND_COLORS.ERROR,
          fill: true,
          tension: 0.4,
          pointRadius: 2,
          pointHoverRadius: 4,
        },
      ],
    };
  }, [performanceData?.cpu, dataPoints]);

  // Request Rate Chart
  const requestChartData = useMemo(() => {
    if (!performanceData?.requests) return null;
    
    const { labels, data: totalData } = generateTimeSeriesData(
      performanceData.requests.total, 
      dataPoints
    );
    const { data: errorData } = generateTimeSeriesData(
      performanceData.requests.errors, 
      dataPoints
    );
    
    return {
      labels,
      datasets: [
        {
          label: 'Total Requests',
          data: totalData,
          backgroundColor: PERFORMANCE_CONFIG.CHART_CONFIG.COLORS.PRIMARY,
          borderColor: PERFORMANCE_CONFIG.CHART_CONFIG.COLORS.PRIMARY,
          borderWidth: 1,
        },
        {
          label: 'Errors',
          data: errorData,
          backgroundColor: PERFORMANCE_CONFIG.CHART_CONFIG.COLORS.ERROR,
          borderColor: PERFORMANCE_CONFIG.CHART_CONFIG.COLORS.ERROR,
          borderWidth: 1,
        },
      ],
    };
  }, [performanceData?.requests, dataPoints]);

  // Common chart options
  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#fff',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          color: PERFORMANCE_CONFIG.CHART_CONFIG.GRID_COLORS.X,
        },
        ticks: {
          maxTicksLimit: 10,
        },
      },
      y: {
        display: true,
        grid: {
          color: PERFORMANCE_CONFIG.CHART_CONFIG.GRID_COLORS.Y,
        },
        beginAtZero: true,
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  };

  // Chart component
  const ChartCard = ({ title, children, height = 300 }) => (
    <Card 
      title={title} 
      className="chart-card"
      style={{ height: height + 80 }}
    >
      <div className="chart-container" style={{ height }}>
        {children}
      </div>
    </Card>
  );

  if (loading) {
    return (
      <Card 
        title="Performance Charts" 
        className={`performance-charts ${className}`}
        style={style}
      >
        <div className="performance-charts-loading">
          <Spin 
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} 
            size="large"
          />
        </div>
      </Card>
    );
  }

  if (!performanceData) {
    return (
      <Card 
        title="Performance Charts" 
        className={`performance-charts ${className}`}
        style={style}
      >
        <div className="performance-charts-no-data">
          <p>No performance data available for charts</p>
        </div>
      </Card>
    );
  }

  return (
    <div className={`performance-charts ${className}`} style={style}>
      <Row gutter={[16, 16]}>
        {/* Event Loop Latency Chart */}
        {eventLoopChartData && (
          <Col xs={24} lg={12}>
            <ChartCard title="Event Loop Latency">
              <Line 
                ref={(ref) => chartRefs.current.eventLoop = ref}
                data={eventLoopChartData} 
                options={{
                  ...commonOptions,
                  scales: {
                    ...commonOptions.scales,
                    y: {
                      ...commonOptions.scales.y,
                      title: {
                        display: true,
                        text: 'Latency (ms)',
                      },
                    },
                  },
                }}
              />
            </ChartCard>
          </Col>
        )}

        {/* Memory Usage Chart */}
        {memoryChartData && (
          <Col xs={24} lg={12}>
            <ChartCard title="Memory Usage">
              <Line 
                ref={(ref) => chartRefs.current.memory = ref}
                data={memoryChartData} 
                options={{
                  ...commonOptions,
                  scales: {
                    ...commonOptions.scales,
                    y: {
                      ...commonOptions.scales.y,
                      max: 100,
                      title: {
                        display: true,
                        text: 'Usage (%)',
                      },
                    },
                  },
                }}
              />
            </ChartCard>
          </Col>
        )}

        {/* CPU Usage Chart */}
        {cpuChartData && (
          <Col xs={24} lg={12}>
            <ChartCard title="CPU Usage">
              <Line 
                ref={(ref) => chartRefs.current.cpu = ref}
                data={cpuChartData} 
                options={{
                  ...commonOptions,
                  scales: {
                    ...commonOptions.scales,
                    y: {
                      ...commonOptions.scales.y,
                      max: 100,
                      title: {
                        display: true,
                        text: 'Usage (%)',
                      },
                    },
                  },
                }}
              />
            </ChartCard>
          </Col>
        )}

        {/* Request Rate Chart */}
        {requestChartData && (
          <Col xs={24} lg={12}>
            <ChartCard title="Request Rate">
              <Bar 
                ref={(ref) => chartRefs.current.requests = ref}
                data={requestChartData} 
                options={{
                  ...commonOptions,
                  scales: {
                    ...commonOptions.scales,
                    y: {
                      ...commonOptions.scales.y,
                      title: {
                        display: true,
                        text: 'Requests',
                      },
                    },
                  },
                }}
              />
            </ChartCard>
          </Col>
        )}
      </Row>
    </div>
  );
});

PerformanceCharts.displayName = 'PerformanceCharts';

export default PerformanceCharts;
